import React from 'react'
import { useState, useEffect } from 'react'
import moment from 'moment'
import { useParams } from 'react-router-dom'

export default function ANCLScheduleMatches() {
  const { seriesid, teamid } = useParams()
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await fetch(
          `https://core-prod-origin.cricclubs.com/core/series/getSeriesList?X-Auth-Token=&clubId=16685&limit=10`
        )

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`)
        }

        const data = await response.json()
        setMatches(data?.data?.fixtureList || [])
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchMatches()
  }, [seriesid, teamid])

  // Format date helper
  const formatDate = dateString => {
    return moment(dateString)
      .format('Do MMM YYYY')
      .replace(/(\d+)(st|nd|rd|th)/, '$1')
  }

  // Loading state
  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center vh-50'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className='alert alert-danger text-center mx-auto my-5' style={{ maxWidth: '600px' }}>
        <h4>Error Loading Matches</h4>
        <p className='mb-0'>{error}</p>
      </div>
    )
  }

  // No matches found
  if (!matches || matches.length === 0) {
    return (
      <div className='text-center py-5'>
        <h4>No Upcoming Matches</h4>
        <p className='text-muted'>Check back later for scheduled matches</p>
      </div>
    )
  }

  return (
    <div className='container py-5'>
      <div className='row justify-content-center'>
        {matches.map((match, index) => (
          <div key={`${match.matchId}-${index}`} className='col-lg-6 col-xl-5 mb-4'>
            <div className='card border-0 shadow-sm h-100 schedule-card'>
              <div className='card-body p-4'>
                <div className='row align-items-center'>
                  {/* Date Section */}
                  <div className='col-12 col-md-3 text-center mb-3 mb-md-0'>
                    <div className='bg-light rounded p-3'>
                      <p className='mb-1 text-muted'>{match.day}</p>
                      <h2 className='mb-1 fw-bold text-primary'>{formatDate(match.date)}</h2>
                      <p className='mb-0 text-muted small'>{match.time}</p>
                    </div>
                  </div>

                  {/* Teams Section */}
                  <div className='col-12 col-md-9'>
                    <div className='d-flex flex-column'>
                      {/* Team 1 */}
                      <div className='d-flex align-items-center mb-3'>
                        <div className='team-logo me-2'>
                          <img
                            src={`https://cricclubs.com${match.t1_logo_file_path}`}
                            alt={match.teamOneName}
                            className='img-fluid'
                            style={{ maxHeight: '40px' }}
                            onError={e => {
                              e.target.onerror = null
                              e.target.src =
                                'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNjY2MiPk5vIExvZ288L3RleHQ+PC9zdmc+'
                            }}
                          />
                        </div>
                        <div className='flex-grow-1'>
                          <h5 className='mb-0'>{match.teamOneName}</h5>
                        </div>
                      </div>

                      {/* VS Separator */}
                      <div className='d-flex justify-content-center my-1'>
                        <span className='badge bg-light text-dark'>VS</span>
                      </div>

                      {/* Team 2 */}
                      <div className='d-flex align-items-center mt-3'>
                        <div className='team-logo me-2'>
                          <img
                            src={`https://cricclubs.com${match.t2_logo_file_path}`}
                            alt={match.teamTwoName}
                            className='img-fluid'
                            style={{ maxHeight: '40px' }}
                            onError={e => {
                              e.target.onerror = null
                              e.target.src =
                                'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDAgMTAwIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2Y1ZjVmNSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNjY2MiPk5vIExvZ288L3RleHQ+PC9zdmc+'
                            }}
                          />
                        </div>
                        <div className='flex-grow-1'>
                          <h5 className='mb-0'>{match.teamTwoName}</h5>
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className='d-flex align-items-center justify-content-center mt-4 pt-2 border-top'>
                      <i className='fas fa-location-dot me-2 text-muted'></i>
                      <span className='text-truncate'>{match.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
